/*
Student Zone Mega Navigation
----------------------------

Features:
- Accessible keyboard navigation
- Arrow navigation within mega menu grid
- Escape / focus-out closes menu
- Hover support on desktop
- Click support on mobile

Structure:
- initMegaNav() builds the navigation DOM from navTree
- attachMegaNavBehaviour() adds interaction behaviour

Maintainer note:
Navigation columns form a grid allowing ArrowUp/Down/Left/Right movement
between links similar to GOV.UK mega navigation behaviour.
*/

//import { cloneNavTreeWithDepthLimit } from "./navigation-utils.js";
import {
  cloneNavTreeWithDepthLimit,
  createNavLink,
} from "./navigation-utils.js";

function attachMegaNavBehaviour() {
  const desktop = window.matchMedia("(min-width: 992px)");
  if (!desktop.matches) return;

  const backdrop = document.querySelector(".sz-nav-backdrop");
  const nav = document.querySelector(".sz-main-nav");
  const navItems = [...document.querySelectorAll(".sz-main-nav__item")];
  if (!navItems.length) return;

  function closeNav() {
    document.querySelectorAll(".sz-main-nav__item.is-open").forEach((item) => {
      item.classList.remove("is-open");

      const link = item.querySelector(".sz-main-nav__link");
      if (link) link.setAttribute("aria-expanded", "false");
    });

    if (backdrop) backdrop.hidden = true;

    document.body.style.overflow = "";
  }

  function openNav(item, button) {
    clearTimeout(closeTimer);
    closeNav();

    item.classList.add("is-open");

    if (button) button.setAttribute("aria-expanded", "true");

    if (backdrop) backdrop.hidden = false;
  }

  navItems.forEach((item, index) => {
    const button = item.querySelector(".sz-main-nav__link");

    if (!button) return;

    /* -----------------------------
       CLICK OPEN
    ----------------------------- */

    button.addEventListener("click", (e) => {
      const isOpen = item.classList.contains("is-open");

      if (!isOpen) {
        e.preventDefault();

        openNav(item, button);

        document.body.style.overflow = "hidden";
      }
    });

    /* -----------------------------
       KEYBOARD SUPPORT
    ----------------------------- */
    const mega = item.querySelector(".sz-mega");
    const hasMega = !!mega;
    let grid = null;

    if (hasMega) {
      const columns = [...mega.querySelectorAll(".sz-mega__col")];
      grid = columns.map((col) => [...col.querySelectorAll("a")]);
    }

    button.addEventListener("keydown", (e) => {
      if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
      }
      const isOpen = item.classList.contains("is-open");

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();

        if (!isOpen) {
          openNav(item, button);

          const firstLink = item.querySelector(".sz-mega a");
          if (firstLink) firstLink.focus();
        }
      }

      if (e.key === "ArrowDown" && hasMega) {
        openNav(item, button);

        const firstLink = item.querySelector(".sz-mega a");
        if (firstLink) firstLink.focus();
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        const next = navItems[index + 1];
        if (next) next.querySelector(".sz-main-nav__link").focus();
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prev = navItems[index - 1];
        if (prev) prev.querySelector(".sz-main-nav__link").focus();
      }
    });

    /* -----------------------------
       MEGA MENU GRID NAVIGATION
    ----------------------------- */

    if (hasMega) {
      mega.addEventListener("keydown", (e) => {
        if (
          ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)
        ) {
          e.preventDefault();
        }

        if (!grid) return;

        let colIndex = -1;
        let rowIndex = -1;

        grid.forEach((col, c) => {
          const r = col.indexOf(document.activeElement);
          if (r !== -1) {
            colIndex = c;
            rowIndex = r;
          }
        });

        if (colIndex === -1) return;

        const currentColumn = grid[colIndex];
        const nextColumn = grid[colIndex + 1];
        const prevColumn = grid[colIndex - 1];

        /* ↓ DOWN */

        if (e.key === "ArrowDown") {
          if (rowIndex < currentColumn.length - 1) {
            currentColumn[rowIndex + 1].focus();
          } else if (nextColumn) {
            nextColumn[0].focus();
          } else {
            const nextTop = navItems[index + 1];
            if (nextTop) {
              nextTop.querySelector(".sz-main-nav__link").focus();
            } else {
              closeNav();
            }
          }
        }

        /* ↑ UP */

        if (e.key === "ArrowUp") {
          if (rowIndex > 0) {
            currentColumn[rowIndex - 1].focus();
          } else {
            closeNav();
            button.focus();
          }
        }

        /* → RIGHT */

        if (e.key === "ArrowRight") {
          if (nextColumn) {
            const target =
              nextColumn[rowIndex] || nextColumn[nextColumn.length - 1];

            target.focus();
          } else {
            const nextTop = navItems[index + 1];

            if (nextTop) {
              closeNav();
              nextTop.querySelector(".sz-main-nav__link").focus();
            }
          }
        }

        /* ← LEFT */

        if (e.key === "ArrowLeft") {
          if (prevColumn) {
            const target =
              prevColumn[rowIndex] || prevColumn[prevColumn.length - 1];

            target.focus();
          } else {
            closeNav();
            button.focus();
          }
        }

        /* HOME */

        if (e.key === "Home") {
          e.preventDefault();
          grid[0][0].focus();
        }

        /* END */

        if (e.key === "End") {
          e.preventDefault();

          const lastColumn = grid[grid.length - 1];
          lastColumn[lastColumn.length - 1].focus();
        }

        /* ESC */

        if (e.key === "Escape") {
          closeNav();
          button.focus();
        }
      });
    }
  });
  nav.addEventListener("focusout", (e) => {
    const stillInside = e.relatedTarget && nav.contains(e.relatedTarget);

    if (!stillInside) {
      closeNav();
    }
  });

  if (desktop.matches) {
    navItems.forEach((item) => {
      const button = item.querySelector(".sz-main-nav__link");

      item.addEventListener("mouseenter", () => {
        openNav(item, button);
      });
    });
  }

  let closeTimer;

  if (nav) {
    nav.addEventListener("mouseleave", () => {
      closeTimer = setTimeout(closeNav, 200);
    });

    nav.addEventListener("mouseenter", () => {
      clearTimeout(closeTimer);
    });
  }

  if (backdrop) {
    backdrop.addEventListener("click", () => {
      closeNav();
    });
  }
}

/* ------------------------------------------------
   BUILD MEGA NAV FROM TREE
------------------------------------------------ */
export default function initMegaNav(state) {
  console.log("INIT MEGA NAV RUNNING");
  const limitedTree = cloneNavTreeWithDepthLimit(state.tree, 3);
  const container = document.querySelector(".sz-main-nav .container");

  if (!container || !limitedTree.length) return;

  const nav = document.createElement("ul");
  nav.className = "sz-main-nav__list";

  if (!Array.isArray(limitedTree)) return;

  limitedTree.forEach((item, index) => {
    const li = document.createElement("li");
    li.className = "sz-main-nav__item";

    const hasChildren = item.children && item.children.length;

    if (hasChildren) li.classList.add("has-sub");

    const link = createNavLink({
      url: item.url,
      label: item.label,
      className: "sz-main-nav__link",
      isActive: state.path.includes(item.url),
    });

    if (hasChildren) {
      const submenuId =
        "submenu-" +
        index +
        "-" +
        item.label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");

      link.setAttribute("aria-expanded", "false");
      link.setAttribute("aria-controls", submenuId);
      link.setAttribute("aria-haspopup", "true");

      const sub = document.createElement("div");
      sub.className = "sz-mega";
      sub.id = submenuId;

      const megaContainer = document.createElement("div");
      megaContainer.className = "container";

      const row = document.createElement("div");
      row.className = "sz-mega__row";

      item.children.forEach((child) => {
        const column = document.createElement("div");
        column.className = "sz-mega__col";

        /* const heading = document.createElement("a");
        heading.href = child.url;
        heading.className = "sz-mega__heading";
        heading.textContent = child.label;
        if (state.path.includes(child.url)) {
          heading.classList.add("is-active");
        } */

        const heading = createNavLink({
          url: child.url,
          label: child.label,
          className: "sz-mega__heading",
          isActive: state.path.includes(child.url),
        });

        column.appendChild(heading);

        if (child.children && child.children.length) {
          const list = document.createElement("ul");
          list.className = "sz-mega__list";

          child.children.forEach((grandchild) => {
            const li = document.createElement("li");

            /* const link = document.createElement("a");
            link.href = grandchild.url;
            link.textContent = grandchild.label;
            if (state.path.includes(grandchild.url)) {
              link.classList.add("is-active");
            } */
            const link = createNavLink({
              url: grandchild.url,
              label: grandchild.label,
              isActive: state.path.includes(grandchild.url),
            });

            li.appendChild(link);
            list.appendChild(li);
          });

          column.appendChild(list);
        }

        row.appendChild(column);
      });

      megaContainer.appendChild(row);

      sub.appendChild(megaContainer);

      li.appendChild(link);
      li.appendChild(sub);
    } else {
      li.appendChild(link);
    }

    nav.appendChild(li);
  });

  container.replaceChildren(nav);

  attachMegaNavBehaviour();
}
